import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Download, Printer, SlidersHorizontal, Vault } from 'lucide-react';
import { formatIqd, fromMillidinars, type BondSource, type BondsRowDto } from '@gc/shared';

import { Collapsible } from '@/components/Collapsible';
import { useAuth } from '@/features/auth/useAuth';
import { ReportRangePicker } from '@/features/reports/ReportRangePicker';
import {
  rangeFromBusinessDay,
  useReportCatalogue,
  type ReportRangeState,
} from '@/features/reports/useReports';
import { signedMoney } from './SafePage';
import {
  downloadBondsCsv,
  employeeNamesOf,
  filterBondsRows,
  rangeToBusinessDates,
  sumBondsRows,
  useBonds,
  type BondsWindow,
  type BusinessDayInfo,
} from './useSafe';

/**
 * Bonds (SPEC §12.1, wave 3): every money movement in the system, whichever
 * of the eight tables recorded it, as one filterable ledger.
 *
 * Follows the StorePage report-screen conventions — the shared
 * `ReportRangePicker`, the gc-report-print scoping, an export gated on
 * `reports.export` — but renders its own table: the bonds feed is not one of
 * the catalogue's `ReportId`s, so `ReportTableView`'s server-side CSV/PDF
 * routes would 404 on it; the CSV here is built client-side from exactly the
 * rows on screen instead.
 *
 * Filtering is client-side over the fetched window on purpose: the chips
 * answer "what am I looking at" questions and must respond instantly, and a
 * night's movements are a few hundred rows.
 */
/**
 * The two halves of the ledger (owner's model): BONDS are account-to-account
 * movements — a safe slip (a cash-drawer drop or top-up, a withdrawal, a
 * deposit; before the cash drawer, a box swept to the safe), an expense or
 * income against an outside party's account. BILLS are the sales flow — the
 * bill, its payments, the old box filings, refills. Discounts appear in
 * NEITHER: they are columns on bills, and live only in the reports built for
 * them. The box sources stay for the dates that hold them.
 */
export const VIEW_SOURCES = {
  bonds: ['BOX_RESET', 'SAFE', 'EXPENSE', 'INCOME'],
  bills: ['BILL', 'PAYMENT', 'BOX_ENTRY', 'REFILL'],
} as const satisfies Record<string, readonly BondSource[]>;

type BondsView = keyof typeof VIEW_SOURCES;

export function BondsPage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  const [view, setView] = useState<BondsView>('bonds');

  // The catalogue supplies the venue's business day for exact instant ->
  // business-date resolution. Bonds viewers without `reports.view` get a 403
  // here — that only degrades the seeding (calendar dates instead of the
  // venue's day), never the money.
  const { catalogue, error: catalogueError } = useReportCatalogue();
  const businessDay: BusinessDayInfo | null = catalogue
    ? {
        openingTime: catalogue.range.openingTime,
        closingTime: catalogue.range.closingTime,
        timeZone: catalogue.range.timeZone,
      }
    : null;

  const [draftRange, setDraftRange] = useState<ReportRangeState | null>(null);
  const [applied, setApplied] = useState<BondsWindow | null>(null);
  const [sources, setSources] = useState<ReadonlySet<BondSource>>(new Set());
  const [employeeName, setEmployeeName] = useState<string | null>(null);

  useEffect(() => {
    if (draftRange) return;
    if (catalogue) {
      const seeded = rangeFromBusinessDay(
        catalogue.range.fromBusinessDate,
        catalogue.range.openingTime,
        catalogue.range.closingTime,
      );
      setDraftRange(seeded);
      setApplied(
        rangeToBusinessDates(seeded, {
          openingTime: catalogue.range.openingTime,
          closingTime: catalogue.range.closingTime,
          timeZone: catalogue.range.timeZone,
        }),
      );
    } else if (catalogueError) {
      // No reports privilege: seed today's calendar date, full day.
      const today = new Date();
      const pad = (value: number) => String(value).padStart(2, '0');
      const date = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
      const seeded = { from: `${date}T00:00`, to: `${date}T23:59` };
      setDraftRange(seeded);
      setApplied({ from: date, to: date });
    }
  }, [catalogue, catalogueError, draftRange]);

  const { data, error, loading } = useBonds(applied);

  const shown = useMemo(() => {
    if (!data) return [];
    // The tab bounds the world; the chips narrow within it. An empty chip
    // selection means "everything in this tab", never "everything".
    const viewSources = new Set<BondSource>(VIEW_SOURCES[view]);
    const effective =
      sources.size > 0
        ? new Set([...sources].filter((source) => viewSources.has(source)))
        : viewSources;
    return filterBondsRows(data.rows, { sources: effective, employeeName });
  }, [data, view, sources, employeeName]);
  const employees = useMemo(() => (data ? employeeNamesOf(data.rows) : []), [data]);

  if (!can('safe.view')) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('safe.bonds.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('safe.noViewPermission')}</p>
      </div>
    );
  }

  const canExport = can('reports.export');

  function toggleSource(source: BondSource): void {
    setSources((current) => {
      const next = new Set(current);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .gc-report-print, .gc-report-print * { visibility: visible !important; }
          .gc-report-print {
            position: absolute !important;
            inset-inline-start: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
          .gc-no-print { display: none !important; }
        }
      `}</style>

      <header className="gc-no-print flex flex-wrap items-center gap-3 px-4 pt-4">
        <h1 className="text-xl font-semibold text-content">{t('safe.bonds.title')}</h1>
        <p className="text-xs text-content-muted">{t('safe.bonds.subtitle')}</p>
        <div className="flex-1" />
        <Link
          to="/safe"
          className="inline-flex items-center gap-2 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
        >
          <Vault className="h-4 w-4" aria-hidden />
          {t('safe.backToSafe')}
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          disabled={!data}
          className="inline-flex items-center gap-2 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover disabled:opacity-40"
        >
          <Printer className="h-4 w-4" aria-hidden />
          {t('safe.bonds.print')}
        </button>
        <button
          type="button"
          onClick={() => applied && downloadBondsCsv(shown, applied)}
          disabled={!data || !canExport || shown.length === 0}
          title={canExport ? undefined : t('reports.noExportPermission')}
          className="inline-flex items-center gap-2 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Download className="h-4 w-4" aria-hidden />
          {t('safe.bonds.csv')}
        </button>
      </header>

      {draftRange ? (
        <div className="gc-no-print px-4 pt-4">
          <ReportRangePicker
            value={draftRange}
            onChange={setDraftRange}
            onFetch={() => setApplied(rangeToBusinessDates(draftRange, businessDay))}
            busy={loading}
            resolved={
              applied ? { fromBusinessDate: applied.from, toBusinessDate: applied.to } : null
            }
          />
        </div>
      ) : null}

      {/* The two halves of the ledger. Switching tabs clears the chips — a
          BILL chip carried into the Bonds tab would silently show nothing. */}
      <div className="gc-no-print flex items-center gap-1.5 px-4 pt-3">
        {(Object.keys(VIEW_SOURCES) as BondsView[]).map((entry) => (
          <button
            key={entry}
            type="button"
            onClick={() => {
              setView(entry);
              setSources(new Set());
            }}
            aria-pressed={view === entry}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
              view === entry
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-subtle text-content-secondary hover:bg-hover'
            }`}
          >
            {t(`safe.bonds.view.${entry}`)}
          </button>
        ))}
      </div>

      {/* Phone: the source chips and the employee filter fold behind one
          Filters button (desktop: the same row as always) — with the range
          picker and the view tabs above them they were a full screen of
          controls before the first ledger row. */}
      <div className="gc-no-print flex flex-wrap items-center gap-2 px-4 pt-3">
        <Collapsible
          label={t('common.filters')}
          icon={SlidersHorizontal}
          count={sources.size + (employeeName ? 1 : 0)}
          storageKey="bonds.filters"
          layout="wrap"
          cap={false}
        >
          <SourceChip
            label={t('safe.bonds.allSources')}
            active={sources.size === 0}
            onClick={() => setSources(new Set())}
          />
          {VIEW_SOURCES[view].map((source) => (
            <SourceChip
              key={source}
              label={t(`safe.bonds.sources.${source}`)}
              active={sources.has(source)}
              onClick={() => toggleSource(source)}
            />
          ))}

          <div className="flex-1" />

          <label className="flex items-center gap-2 text-sm text-content-secondary">
            {t('safe.bonds.employeeFilter')}
            <select
              value={employeeName ?? ''}
              onChange={(event) => setEmployeeName(event.target.value || null)}
              className="rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary"
            >
              <option value="">{t('safe.bonds.allEmployees')}</option>
              {employees.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </Collapsible>
      </div>

      <div className="min-h-0 flex-1 px-4 pb-6 pt-4">
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : loading && !data ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : data ? (
          <div className="gc-report-print">
            <p className="pb-2 text-xs text-content-muted">
              {t('safe.bonds.rowCount', { shown: shown.length, total: data.rows.length })}
            </p>
            <BondsTable rows={shown} currencySymbol={currencySymbol} />
            <p className="pt-2 text-xs text-content-muted">{t('safe.bonds.overlapNote')}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SourceChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
        active
          ? 'border-primary bg-primary/15 font-medium text-content'
          : 'border-subtle text-content-secondary hover:bg-hover'
      }`}
    >
      {label}
    </button>
  );
}

/**
 * The ledger table. Exported for tests: signs render with an explicit "+" for
 * money in and a highlighted negative for money out, the reference column
 * carries voucher/invoice numbers, and the footer totals EXACTLY the rows
 * shown — a filtered view must never footer the unfiltered sum.
 *
 * Read-only, every row (2026-10-12): the box-reset bond editor went with the
 * box. A slip is corrected by a new slip, never rewritten or deleted —
 * deleting the newest one handed its number to the next movement.
 */
export function BondsTable({
  rows,
  currencySymbol,
}: {
  rows: readonly BondsRowDto[];
  currencySymbol: string;
}) {
  const { t, i18n } = useTranslation();
  const money = (value: string) =>
    formatIqd(fromMillidinars(value), { currencySymbol, locale: i18n.language });

  if (rows.length === 0) {
    return <p className="text-sm text-content-muted">{t('safe.bonds.empty')}</p>;
  }

  return (
    <div className="overflow-x-auto rounded-card border border-subtle">
      <table className="w-full min-w-max text-sm">
        <thead className="bg-overlay text-[10px] uppercase tracking-[0.14em] text-content-muted">
          <tr>
            <Th>{t('safe.bonds.columns.occurredAt')}</Th>
            <Th>{t('safe.bonds.columns.source')}</Th>
            <Th>{t('safe.bonds.columns.reference')}</Th>
            <Th>{t('safe.bonds.columns.description')}</Th>
            <Th>{t('safe.bonds.columns.employee')}</Th>
            <Th>{t('safe.bonds.columns.customer')}</Th>
            <Th>{t('safe.bonds.columns.method')}</Th>
            <Th>{t('safe.bonds.columns.businessDate')}</Th>
            <Th align="end">{t('safe.bonds.columns.amount')}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.source}:${row.id}`} className="border-t border-subtle">
              <Td>{new Date(row.occurredAt).toLocaleString()}</Td>
              <Td>{t(`safe.bonds.sources.${row.source}`)}</Td>
              <Td>{row.reference ?? '—'}</Td>
              <Td>{row.description}</Td>
              <Td>{row.employeeName ?? '—'}</Td>
              <Td>{row.customerName ?? '—'}</Td>
              <Td>{row.method ? t(`safe.bonds.methods.${row.method}`, row.method) : '—'}</Td>
              <Td>{row.businessDate}</Td>
              <Td align="end" strong>
                <span className={row.amountMd.startsWith('-') ? 'text-danger' : undefined}>
                  {signedMoney(row.amountMd, money)}
                </span>
              </Td>
            </tr>
          ))}
          <tr className="border-t border-strong bg-raised font-semibold">
            <Td>{t('safe.bonds.total')}</Td>
            <Td />
            <Td />
            <Td />
            <Td />
            <Td />
            <Td />
            <Td />
            <Td align="end">{signedMoney(sumBondsRows(rows), money)}</Td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'end' }) {
  return (
    <th
      className={`whitespace-nowrap px-3 py-2 font-medium ${
        align === 'end' ? 'text-end' : 'text-start'
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  strong,
}: {
  children?: React.ReactNode;
  align?: 'end';
  strong?: boolean;
}) {
  return (
    <td
      className={[
        'px-3 py-2',
        align === 'end' ? 'tabular text-end' : 'text-start',
        strong ? 'font-medium text-content' : 'text-content-secondary',
      ].join(' ')}
    >
      {children}
    </td>
  );
}
